import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Stack,
  CircularProgress,
  styled,
  Snackbar,
  SnackbarContent,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Box,
  Card,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useResavenueRooms } from "../hooks/useResavenueRooms";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ResavenueRatePlanMapping from "./ResavenueRatePlanMapping";
import { errorColor, successColor } from "../../sdk";
import { Close } from "@mui/icons-material";

const roomTypes = [
  { value: "single", label: "Single" },
  { value: "double", label: "Double" },
  { value: "triple", label: "Triple" },
  { value: "quad", label: "Quad" },
];

// Sortable Room Item Component
const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
    opacity: isDragging ? 0.5 : 1,
    borderRadius: "8px",
  };

  return (
    <RoomStack ref={setNodeRef} style={style}>
      <Stack flex={1}>{children({ attributes, listeners })}</Stack>
    </RoomStack>
  );
};

const ResavenueRoomMapping = () => {
  const {
    roomsData,
    setRoomsData,
    isFetchingRooms,
    snackbarMsg,
    setSnackBarMsg,
    addNewRoom,
    addNewRoomData,
    setAddNewRoom,
    handleNewRoomChange,
    isRoomDataIncomplete,
    addNewRatePlan,
    newRatePlanData,
    setAddNewRatePlan,
    handleNewRatePlanChange,
    isRatePlanDataIncomplete,
    createRoomOrRatePlan,
    handleEditRateplan,
    changeHandlerRatePlan,
    updateRatePlan,
    changeExternalSequence,
    changeRatePlanSequence,
    fetchRooms, 
  } = useResavenueRooms();

  const sensors = useSensors(useSensor(PointerSensor));

  // Room reorder handler
  const handleRoomDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setRoomsData((prev) => {
      const oldIndex = prev.findIndex((r) => r.roomId === active.id);
      const newIndex = prev.findIndex((r) => r.roomId === over.id);
      changeExternalSequence(oldIndex + 1, newIndex + 1);

      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  if (isFetchingRooms) {
    return (
      <Stack alignItems="center" justifyContent="center" p={4} height="50vh">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Wrapper>
      <Stack className="header">
        <Stack className="right-section">
          <Button
            variant="contained"
            className="add-room-button"
            onClick={() => setAddNewRoom(true)}
          >
            + Add Room
          </Button>
        </Stack>
      </Stack>

      {addNewRoom && (
        <CreateRoomCard variant="outlined">
          <Typography variant="h6" align="center" fontWeight="bold" mb={2}>
            Create New Room
          </Typography>
          <IconButton
            onClick={() => setAddNewRoom(null)}
            className="close-button"
          >
            <Close />
          </IconButton>

          {/* Room Details */}
          <RowStack>
            <TextField
              label="Room ID"
              value={addNewRoomData.roomId || ""}
              onChange={(e) => handleNewRoomChange("roomId", e.target.value)}
            />
            <TextField
              label="Room Name"
              value={addNewRoomData.roomName || ""}
              onChange={(e) => handleNewRoomChange("roomName", e.target.value)}
            />
            <TextField
              label="Rate Plan ID"
              value={addNewRoomData.roomRateGroupCode || ""}
              onChange={(e) =>
                handleNewRoomChange("roomRateGroupCode", e.target.value)
              }
            />
            <TextField
              label="Rate Plan Name"
              value={addNewRoomData.roomRatePlanName || ""}
              onChange={(e) =>
                handleNewRoomChange("roomRatePlanName", e.target.value)
              }
            />
            <TextField
              label="Promotional Markup (%)"
              type="number"
              value={addNewRoomData.promotionalDiscount ?? ''}
              onChange={(e) =>
                handleNewRoomChange(
                  "promotionalDiscount",
                  e.target.value ? Number(e.target.value) : null
                )
              }
            />
            <Autocomplete
              value={
                roomTypes.find(
                  (rt) => rt.value === addNewRoomData.occupancyType
                ) || null
              }
              onChange={(_, newValue) =>
                handleNewRoomChange(
                  "occupancyType",
                  newValue ? newValue.value : null
                )
              }
              options={roomTypes}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField {...params} label="Occupancy" fullWidth />
              )}
              sx={{ minWidth: 200 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={addNewRoomData.isBaseRoom || false}
                  onChange={() =>
                    handleNewRoomChange(
                      "isBaseRoom",
                      !addNewRoomData.isBaseRoom
                    )
                  }
                />
              }
              label="Is Base Rate Plan"
            />
          </RowStack>

          {/* Rate Plan Details */}
          <Stack>
            <ActionRow>
              <Box sx={{ flexGrow: 1 }} />

              <SaveButton
                variant="contained"
                disabled={isRoomDataIncomplete}
                onClick={() => createRoomOrRatePlan(true, addNewRoomData)}
              >
                Save
              </SaveButton>
            </ActionRow>
          </Stack>
        </CreateRoomCard>
      )}

      {/* Room-level drag context */}
      <Stack className="room-mappings">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleRoomDragEnd}
        >
          <SortableContext
            items={roomsData.map((r) => r.roomId)}
            strategy={verticalListSortingStrategy}
          >
            {roomsData.map((room, roomIndex) => (
              <SortableItem key={room.roomId} id={room.roomId}>
                {({ attributes, listeners }) => (
                  <CustomAccordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        className="room-header"
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        {/* Drag handle now inside summary */}
                        <DragIndicatorIcon
                          {...attributes}
                          {...listeners}
                          sx={{ cursor: "grab", color: "gray" }}
                        />
                        <Typography fontWeight="bold">
                          {`${room.roomName} (Room ID: ${room.roomId})`}
                        </Typography>
                        <Stack className="rateplan-count">
                          {room.ratePlans.length} rate plans
                        </Stack>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      {/* RatePlan Section */}
                      <ResavenueRatePlanMapping
                        room={room}
                        roomTypes={roomTypes}
                        setSnackBarMsg={setSnackBarMsg}
                        fetchRooms={fetchRooms}
                        roomIndex={roomIndex}
                        setRoomsData={setRoomsData}
                        addNewRatePlan={addNewRatePlan}
                        newRatePlanData={newRatePlanData}
                        setAddNewRatePlan={setAddNewRatePlan}
                        handleNewRatePlanChange={handleNewRatePlanChange}
                        createRoomOrRatePlan={createRoomOrRatePlan}
                        updateRatePlan={updateRatePlan}
                        handleEditRateplan={handleEditRateplan}
                        isRatePlanDataIncomplete={isRatePlanDataIncomplete}
                        changeHandlerRatePlan={changeHandlerRatePlan}
                        changeExternalSequence={changeExternalSequence}
                        changeRatePlanSequence={changeRatePlanSequence}
                      />
                    </AccordionDetails>
                  </CustomAccordion>
                )}
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
      </Stack>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={snackbarMsg.open}
        autoHideDuration={6000}
        onClose={() => {
          setSnackBarMsg({
            ...snackbarMsg,
            open: false,
          });
        }}
      >
        <SnackbarContent
          sx={{
            backgroundColor: snackbarMsg.isError ? errorColor : successColor,
            textTransform: "capitalize",
          }}
          message={snackbarMsg.message}
        />
      </Snackbar>
    </Wrapper>
  );
};

export default ResavenueRoomMapping;

// Styled Components

const Wrapper = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  ".header": {
    width: "100%",
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
  },
  ".left-section": {
    display: "flex",
    flexDirection: "column",
  },
  ".header-title": {
    fontSize: theme.spacing(2.5),
    fontWeight: 600,
  },
  ".header-body": {
    fontSize: theme.spacing(1.75),
    color: "#64748B",
  },
  ".add-room-button": {
    textTransform: "capitalize",
    fontSize: theme.spacing(1.75),
    fontWeight: 500,
  },
  ".room-mappings": {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  ".room-header": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  ".rateplan-count": {
    padding: theme.spacing(0.75),
    fontSize: theme.spacing(1.75),
    fontWeight: 500,
    borderRadius: theme.spacing(0.5),
    color: "#ffffff",
    background: "#6B7280",
  },
}));

const RoomStack = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  background: "#ffffff",
  padding: theme.spacing(2.5),
}));

const CustomAccordion = styled(Accordion)(({ theme }) => ({
  border: "none",
  boxShadow: "none",
  background: "transparent",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: 0,
  },
}));

const CreateRoomCard = styled(Card)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  width: "100%",
  marginTop: theme.spacing(2),
  ".close-button": {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}));

const RowStack = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ActionRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(2),
}));

// Save button styling
const SaveButton = styled(Button)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
}));
